import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import { BookingContext } from './NewBooking'
import { GlobalContext } from '../../../App'
import { useNavigate } from 'react-router-dom'
import API from '../../../services/API'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import {
  dropoffJourneyTypes,
  optionalJourneyTypes,
  pickupJourneyTypes,
} from './PickupDropoff'
import { Typography } from '@mui/material'
import ErrorModal from '../../../components/Modals/Error'

const Submit = ({ setEditMode, loadBooking, onSubmitted }) => {
  const { formData, existingBooking, baseOptions, orgHasWards, selectedPatient } =
    useContext(BookingContext)
  const { setToast } = useContext(GlobalContext)
  const [required, setRequired] = useState(false)
  const [submitLoading, setSubmitLoading] = useState(false)
  const [noGeocodeError, setNoGeocodeError] = useState(null)
  const [submitError, setSubmitError] = useState(null)
  const navigate = useNavigate()

  useEffect(() => {
    if (required) {
      setRequired(false)
    }
  }, [formData])

  const asId = (value) => value?._id ?? value ?? null

  // Where each missing item lives in the form, so the chips can jump there
  const sectionForItem = {
    'Patient Details': 'booking-section-patient',
    'Requesting Organisation': 'booking-section-requester',
    'Requesting Ward': 'booking-section-requester',
    'Journey Type': 'booking-section-pickupdropoff',
    'Patient Ready Time': 'booking-section-pickupdropoff',
    'Target Arrival Time': 'booking-section-pickupdropoff',
    'Pickup/Dropoff Time': 'booking-section-pickupdropoff',
    'Pickup Address': 'booking-section-pickupdropoff',
    'Dropoff Address': 'booking-section-pickupdropoff',
    'Pickup Hospital / Organisation': 'booking-section-pickupdropoff',
    'Dropoff Hospital / Organisation': 'booking-section-pickupdropoff',
    'Patient Home Address (Pickup)': 'booking-section-patient',
    'Patient Home Address (Dropoff)': 'booking-section-patient',
    'Return Journey Pickup Time': 'booking-section-return',
    'Journey Assignment': 'booking-section-assignment',
    'Base Location': 'booking-section-billing',
  }

  const scrollToItem = (item) => {
    const section = document.getElementById(sectionForItem[item])
    section?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const stopOrganisation = (stop = {}) =>
    asId(stop?.organisationAsAddress) ||
    asId(stop?.when?.organisationAsAddress) ||
    null

  const prepareStop = (stop = {}, fallbackWard = null, customerOrg = null) => {
    const organisationAsAddress = stopOrganisation(stop)

    // The requesting ward is only a sensible stop address at the customer's
    // own hospital, not at some other organisation.
    const isCustomerHospital =
      !organisationAsAddress ||
      String(organisationAsAddress) === String(customerOrg || '')

    return {
      ...stop,
      organisationAsAddress,
      wardDepartmentAsAddress:
        asId(stop?.wardDepartmentAsAddress) ||
        (stop?.locationType === 'Hospital' && isCustomerHospital
          ? asId(fallbackWard)
          : null),
    }
  }

  const handleSubmit = async () => {
    setSubmitLoading(true)
    setSubmitError(null)
    setNoGeocodeError(null)
    const itemsRequired = []
    !formData?.patient && itemsRequired.push('Patient Details')
    !formData?.patientIsCustomer &&
      !formData?.organisation &&
      itemsRequired.push('Requesting Organisation')
    // A ward is only required when the requesting org actually has wards —
    // some customer orgs (e.g. repatriation brokers) have none
    !formData?.patientIsCustomer &&
      orgHasWards !== false &&
      !formData?.wardDepartment &&
      itemsRequired.push('Requesting Ward')
    !formData?.journeyType && itemsRequired.push('Journey Type')
    if (
      pickupJourneyTypes.includes(formData?.journeyType) &&
      (!formData?.pickup?.when?.patientReadyFrom ||
        !moment(formData?.pickup?.when?.patientReadyFrom).isValid())
    ) {
      itemsRequired.push('Patient Ready Time')
    }
    if (
      dropoffJourneyTypes.includes(formData?.journeyType) &&
      (!formData?.dropoff?.when?.targetArrivalTime ||
        !moment(formData?.dropoff?.when?.targetArrivalTime).isValid()
      )
    ) {
      itemsRequired.push('Target Arrival Time')
    }
    if (
      optionalJourneyTypes.includes(formData?.journeyType) &&
      (!formData?.dropoff?.when?.targetArrivalTime ||
        !moment(formData?.dropoff?.when?.targetArrivalTime).isValid()) &&
      (!formData?.pickup?.when?.patientReadyFrom ||
        !moment(formData?.pickup?.when?.patientReadyFrom).isValid())
    ) {
      itemsRequired.push('Pickup/Dropoff Time')
    }
    if (
      formData?.pickup?.locationType === 'Other' &&
      (!formData?.pickup?.addressLine1 || !formData?.pickup?.postCode)
    ) {
      itemsRequired.push('Pickup Address')
    }
    if (
      formData?.dropoff?.locationType === 'Other' &&
      (!formData?.dropoff?.addressLine1 || !formData?.dropoff?.postCode)
    ) {
      itemsRequired.push('Dropoff Address')
    }
    if (
      formData?.pickup?.locationType === 'Hospital' &&
      !stopOrganisation(formData?.pickup)
    ) {
      itemsRequired.push('Pickup Hospital / Organisation')
    }
    if (
      formData?.dropoff?.locationType === 'Hospital' &&
      !stopOrganisation(formData?.dropoff)
    ) {
      itemsRequired.push('Dropoff Hospital / Organisation')
    }
    // A "Patient's Home Address" stop can't geocode when the patient record
    // has no address — catch it here instead of a server error after submit
    const patientDemographics = selectedPatient?.demographics || {}
    const patientHasAddress =
      !!patientDemographics.addressLine1 && !!patientDemographics.postCode
    if (
      formData?.pickup?.locationType === 'Patient Address' &&
      !patientHasAddress
    ) {
      itemsRequired.push('Patient Home Address (Pickup)')
    }
    if (
      formData?.dropoff?.locationType === 'Patient Address' &&
      !patientHasAddress
    ) {
      itemsRequired.push('Patient Home Address (Dropoff)')
    }
    if (formData?.returnJourney?.required) {
      if (
        !formData?.returnJourney?.pickup?.when?.patientReadyFrom ||
        !moment(
          formData?.returnJourney?.pickup?.when?.patientReadyFrom,
        ).isValid()
      ) {
        itemsRequired.push('Return Journey Pickup Time')
      }
    }
    if (formData?.assignment?.enabled && !formData?.selectedDuty) {
      itemsRequired.push('Journey Assignment')
    }
    if (
      !existingBooking &&
      (baseOptions?.length ?? 0) > 1 &&
      !formData?.baseLocation
    ) {
      itemsRequired.push('Base Location')
    }

    if (itemsRequired.length > 0) {
      setRequired(itemsRequired)
      setSubmitLoading(false)
      scrollToItem(itemsRequired[0])
      return
    }

    const getGeocodeError = (res) => {
      const code = res?.data?.errorCode || res?.data?.error
      return ['noMatchingGeocode-pickup', 'noMatchingGeocode-dropoff'].includes(
        code,
      )
        ? code
        : null
    }

    const handleFailedResponse = (res) => {
      const geocodeError = getGeocodeError(res)

      if (geocodeError) {
        setNoGeocodeError(geocodeError)
        return
      }

      setSubmitError(
        res?.data?.error ||
          'Unable to create booking. Please check the form and try again.',
      )
    }

    const submissionData = {
      ...formData,
      organisation: asId(formData?.organisation),
      wardDepartment: asId(formData?.wardDepartment),
      pickup: prepareStop(
        formData?.pickup,
        formData?.wardDepartment,
        asId(formData?.organisation),
      ),
      dropoff: prepareStop(
        formData?.dropoff,
        formData?.wardDepartment,
        asId(formData?.organisation),
      ),
    }

    try {
      if (existingBooking) {
        const res = await API.put(
          `/pts/edit-booking/${existingBooking._id}`,
          submissionData,
          {
            skipGlobalErrorHandler: true,
          },
        )

        if (res.status === 200) {
          setEditMode(false)
          loadBooking()
        } else {
          handleFailedResponse(res)
        }
      } else {
        const res = await API.post('/pts/new-booking', submissionData, {
          skipGlobalErrorHandler: true,
        })

        if (res.status === 200) {
          if (onSubmitted) {
            onSubmitted(res.data)
          } else {
            navigate(`/pts/bookings/${res.data}#billing-actions`)
          }
        } else {
          handleFailedResponse(res)
        }
      }
    } catch (e) {
      console.error('returned error', e)
      setSubmitError('Unable to contact the server. Please try again.')
      setToast({
        message: 'Unable to create booking. Please try again.',
        severity: 'error',
      })
    } finally {
      setSubmitLoading(false)
    }
  }

  return (
    <Stack gap={1}>
      {required?.length > 0 && (
        <Box
          sx={{
            borderRadius: 1,
            border: '2px solid',
            borderColor: 'error.light',
            backgroundColor: 'white',
            p: 2,
          }}
        >
          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: 'error.main',
                }}
              />
              <Typography fontWeight={600}>Required fields missing</Typography>
            </Stack>

            <Stack direction="row" gap={1} flexWrap="wrap">
              {required.map((x, i) => (
                <Box
                  component="button"
                  type="button"
                  onClick={() => scrollToItem(x)}
                  sx={{
                    borderRadius: 1.5,
                    backgroundColor: 'white',
                    border: '1px solid',
                    borderColor: 'grey.300',
                    cursor: 'pointer',
                    font: 'inherit',
                    '&:hover': {
                      borderColor: 'error.main',
                      backgroundColor: 'rgba(180,35,24,0.04)',
                    },
                  }}
                  key={i}
                  px={2}
                >
                  {x} ↑
                </Box>
              ))}
            </Stack>
          </Stack>
        </Box>
      )}
      {submitError && (
        <Alert severity="error" onClose={() => setSubmitError(null)}>
          <AlertTitle>
            {existingBooking
              ? 'Unable to save booking'
              : 'Unable to create booking'}
          </AlertTitle>
          {submitError}
        </Alert>
      )}
      <Box display="flex" justifyContent="flex-end" pt={2}>
        <Button
          variant="contained"
          size="large"
          sx={{
            borderRadius: 999,
            px: 5,
            py: 1.2,
            textTransform: 'none',
            fontWeight: 600,
          }}
          onClick={handleSubmit}
          disabled={submitLoading}
        >
          🚑 {submitLoading
            ? existingBooking
              ? 'Saving...'
              : 'Creating...'
            : existingBooking
              ? 'Edit Booking'
              : 'Create Booking'}
        </Button>
      </Box>
      <ErrorModal
        open={noGeocodeError}
        noAutoClose
        onClose={() => setNoGeocodeError(null)}
        title="Address Not Found"
        message={(() => {
          const isPickup = noGeocodeError === 'noMatchingGeocode-pickup'
          const stop = isPickup ? formData?.pickup : formData?.dropoff
          const addressText = [
            stop?.addressLine1,
            stop?.addressLine2,
            stop?.town,
            stop?.city,
            stop?.postCode,
          ]
            .filter(Boolean)
            .join(', ')
          return `Sorry, but we were unable to find the ${
            isPickup ? 'Pickup' : 'Dropoff'
          } address${
            addressText ? ` (${addressText})` : ''
          } on the map. Please check and edit the ${
            isPickup ? 'pickup' : 'dropoff'
          } address, then try again.`
        })()}
      />
    </Stack>
  )
}

export default Submit
